import React, { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import SearchBar from "../../components/Template/SearchBar";
import FilterBar from "../../components/Template/FilterBar";
import TemplatesTable from "../../components/Template/TemplatesTable";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserPermissions } from "../../redux/Slice/User/userDetailsSlice";
import AuthView from "../../utils/AuthView";
import { toast } from "react-hot-toast";
import { getAuthToken } from "../../utils/getAuthToken";
import { checkSubscriptionActive } from "../../utils/subscriptionError";

export const TemplatesHome = () => {
  const { accountid } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userPermissions } = useSelector((state) => state.userDetails);

  // Check if user has permission to view templates
  const canViewTemplates = userPermissions?.permissions?.Templates?.View || false;

  // State for search and filters
  const [searchQuery, setSearchQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [language, setLanguage] = useState("All");
  const [status, setStatus] = useState("All");

  useEffect(() => {
    dispatch(fetchUserPermissions());
  }, [dispatch]);

  const handleCreateTemplate = async () => {
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
    navigate(`/home/${accountid}/templates/create`);
  };

  // Handler functions
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
  };

  const handleLanguageChange = (value) => {
    setLanguage(value);
  };

  const handleStatusChange = (value) => {
    setStatus(value);
  };

  // If user doesn't have permission to view templates
  if (!canViewTemplates) {
    return <AuthView />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: { xs: "stretch", sm: "stretch", md: "center", lg: "center", xl: "center" },
        justifyContent: { xs: "flex-start", sm: "flex-start", md: "center", lg: "center", xl: "center" }, // Flex-start for xs/sm to prevent white space
        // Fill Home outlet; minHeight 0 + flex 1 lets nested table scroll (xs/sm with BottomNav; md+ unchanged visually)
        minHeight: 0,
        flex: 1,
        height: "100%", // Constrain height to parent
        maxHeight: "100%", // Prevent overflow
        width: "100%",
        backgroundColor: (theme) => theme.palette.background.default,
        padding: { xs: "10px 10px 0 10px", sm: "15px 20px 0 20px", md: "20px 0", lg: "20px 0", xl: "20px 0" }, // Remove bottom padding to prevent white space
        // BottomNav already sits below outlet in Home; small inset only (same idea as Contacts page)
        paddingBottom: { xs: "4px", sm: "4px", md: "20px", lg: "20px", xl: "20px" },
        marginBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, // No bottom margin to prevent white space
        marginTop: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, // No top margin
        boxSizing: "border-box",
        overflowY: { xs: "hidden", sm: "hidden", md: "hidden", lg: "hidden", xl: "hidden" },
        overflowX: "hidden", // Prevent horizontal scrolling
        // Hide scrollbar but keep scrolling functionality for xs and sm
        scrollbarWidth: { xs: "none", sm: "none", md: "none", lg: "none", xl: "none" }, // Firefox - hide for all
        msOverflowStyle: { xs: "none", sm: "none", md: "none", lg: "none", xl: "none" }, // IE and Edge (old) - hide for all
        "&::-webkit-scrollbar": {
          width: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, // Hide scrollbar on all sizes
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: { xs: "flex-start", sm: "flex-start", md: "center", lg: "center", xl: "center" },
          alignItems: { xs: "stretch", sm: "stretch", md: "center", lg: "center", xl: "center" },
          gap: { xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }, // Further reduced gap for sm to prevent white space
          width: { xs: "100%", sm: "100%", md: "90%", lg: "85%", xl: "80%" },
          height: { xs: "100%", sm: "100%", md: "100%", lg: "100%", xl: "100%" },
          flex: 1,
          minHeight: 0,
          maxHeight: { xs: "none", sm: "none", md: "calc(100vh - 104px)", lg: "calc(100vh - 104px)", xl: "calc(100vh - 104px)" }, // Remove maxHeight constraint for xs/sm
          overflow: { xs: "hidden", sm: "hidden", md: "visible", lg: "visible", xl: "visible" },
          marginBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, // No margin to prevent white space
          paddingBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 }, // No padding to prevent white space
          marginLeft: { xs: 0, sm: 0, md: "auto", lg: "auto", xl: "auto" },
          marginRight: { xs: 0, sm: 0, md: "auto", lg: "auto", xl: "auto" },
          boxSizing: "border-box",
        }}
      >
        {/* Search and Filter Section */}
        <Box
          sx={{
            display: "flex",
            width: {
              xs: "calc(100% - 20px)",
              sm: "calc(100% - 40px)",
              md: "100%",
              lg: "100%",
              xl: "100%",
            },
            marginLeft: { xs: "10px", sm: "20px", md: 0, lg: 0, xl: 0 },
            marginRight: { xs: "10px", sm: "20px", md: 0, lg: 0, xl: 0 },
            marginTop: { xs: 2, sm: 2.5, md: 3, lg: 3, xl: 3 }, // extra top space for all screen sizes
            alignItems: {
              xs: "stretch",
              sm: "stretch",
              md: "center",
              lg: "center",
              xl: "center",
            },
            justifyContent: {
              xs: "flex-start",
              sm: "flex-start",
              md: "space-between",
              lg: "space-between",
              xl: "space-between",
            },
            flexDirection: { xs: "column", sm: "column", md: "row", lg: "row", xl: "row" },
            gap: { xs: 0.75, sm: 1, md: 1, lg: 1, xl: 1 },
            padding: {
              xs: "16px 8px 10px 8px", // extra top padding on xs
              sm: "16px 10px 12px 10px", // extra top padding on sm
              md: "10px 14px",
              lg: "13px 16px",
              xl: "13px 0",
            },
            backgroundColor: (theme) => theme.palette.background.paper,
            borderRadius: "8px",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 2px 4px rgba(0, 0, 0, 0.2)"
                : "0 2px 4px rgba(0, 0, 0, 0.1)",
            boxSizing: "border-box",
          }}
        >
          {/* First row: search + filters */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: { xs: "100%", sm: "100%", md: "auto", lg: "auto", xl: "auto" },
              gap: { xs: 0.5, sm: 0.75, md: 1, lg: 1, xl: 1 },
              flex: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
            }}
          >
            {/* Search Bar */}
            <SearchBar onSearch={handleSearch} />

            {/* Filter Bar */}
            <FilterBar
              category={category}
              language={language}
              status={status}
              onCategoryChange={handleCategoryChange}
              onLanguageChange={handleLanguageChange}
              onStatusChange={handleStatusChange}
            />
          </Box>

          {/* Second row on xs/sm: Create Template Button (same row on md+) */}
          <Button
            onClick={handleCreateTemplate}
            variant="contained"
            sx={{
              borderRadius: "12px",
              mr: { xs: 0, sm: 0, md: 1, lg: 1, xl: 1 },
              ml: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
              width: { xs: "100%", sm: "100%", md: "auto", lg: "auto", xl: "auto" },
              backgroundColor: "#276d5d",
              color: "#fff",
              textTransform: "none",
              fontSize: { xs: "11px", sm: "12px", md: "11px", lg: "14px", xl: "14px" },
              padding: {
                xs: "6px 12px",
                sm: "7px 14px",
                md: "6px 12px",
                lg: "10px 20px",
                xl: "10px 20px",
              },
              minWidth: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
              justifyContent: {
                xs: "center",
                sm: "center",
                md: "center",
                lg: "center",
                xl: "center",
              },
              textAlign: {
                xs: "center",
                sm: "center",
                md: "center",
                lg: "center",
                xl: "center",
              },
              mb: { xs: 1, sm: 1, md: 0, lg: 0, xl: 0 }, // space below button for xs/sm
              ":hover": {
                backgroundColor: "#1e594b",
              },
            }}
          >
            Create Template
          </Button>
        </Box>

        {/* Templates Table Section */}
        <Box
          sx={{
            flex: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
            minHeight: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
            width: { xs: "calc(100% - 20px)", sm: "calc(100% - 40px)", md: "100%", lg: "100%", xl: "100%" },
            marginLeft: { xs: "10px", sm: "20px", md: 0, lg: 0, xl: 0 },
            marginRight: { xs: "10px", sm: "20px", md: 0, lg: 0, xl: 0 },
            height: { xs: "auto", sm: "auto", md: "100%", lg: "100%", xl: "100%" },
            maxHeight: { xs: "none", sm: "none", md: "none", lg: "none", xl: "none" },
            backgroundColor: "transparent", // Transparent background to prevent white box showing
            borderRadius: { xs: 0, sm: 0, md: "8px", lg: "8px", xl: "8px" }, // Remove border radius for xs/sm
            boxShadow: "none", // Remove box shadow for xs/sm to prevent white box
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxSizing: "border-box",
            padding: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
            marginBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
            marginTop: { xs: 1.5, sm: 2, md: 0, lg: 0, xl: 0 }, // small gap under filters
          }}
        >
          <TemplatesTable
            searchQuery={searchQuery}
            category={category}
            language={language}
            status={status}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TemplatesHome;
